/**
 * Which token this TAB talks to the API with (DOP-562).
 *
 * Ordinarily that is the signed-in user's `access_token` in localStorage. A
 * Doppler operator in a support session ("view as user") carries a second,
 * short-lived token, and it lives in sessionStorage on purpose:
 *
 *   - it is per-tab, so it never replaces the operator's own login to this
 *     deployment, and a customer's browser profile never inherits it;
 *   - it dies with the tab.
 *
 * While it is present it wins. Every place that reads the bearer token goes
 * through `getSessionToken()`, or the tab would speak as two people at once.
 */

const ACCESS_TOKEN_KEY = "access_token";
const SUPPORT_TOKEN_KEY = "support_access_token";

export function getSupportToken(): string | null {
	try {
		return sessionStorage.getItem(SUPPORT_TOKEN_KEY);
	} catch {
		// Storage can throw (blocked site data, some private windows). No
		// support session is the safe reading.
		return null;
	}
}

export function setSupportToken(token: string): void {
	sessionStorage.setItem(SUPPORT_TOKEN_KEY, token);
}

export function clearSupportToken(): void {
	try {
		sessionStorage.removeItem(SUPPORT_TOKEN_KEY);
	} catch {
		// Nothing to clear.
	}
}

export function isSupportSession(): boolean {
	return getSupportToken() !== null;
}

export function getSessionToken(): string | null {
	return getSupportToken() ?? localStorage.getItem(ACCESS_TOKEN_KEY);
}

const CONSOLE_URL_KEY = "support_console_url";

/** Where Exit leads: the internal support console, when the server names it. */
export function rememberConsoleUrl(url: string | null | undefined): void {
	try {
		if (url) sessionStorage.setItem(CONSOLE_URL_KEY, url);
	} catch {
		// The link back is a convenience; the session works without it.
	}
}

export function rememberedConsoleUrl(): string | null {
	try {
		const url = sessionStorage.getItem(CONSOLE_URL_KEY);
		return url && /^https?:\/\//.test(url) ? url : null;
	} catch {
		return null;
	}
}
